/**
 * SeedDrive V2 — Workflow State Definitions
 *
 * Central definitions for all workflow state models used in the prototype.
 * Pages import these to render status chips, workflow steppers, and gated CTAs.
 *
 * Prototype implementation: states are visual only.
 * No backend enforcement — mock state is assigned in mockData.js.
 *
 * Two models defined:
 *   1. IC_SUBMISSION_STATES — internal deal governance (DRAFT → APPROVED)
 *   2. MARKETPLACE_ACCESS_STATES — external investor progressive disclosure
 */

// ─── IC Submission States ─────────────────────────────────────────────────────
// Represents the lifecycle of an internal IC submission for a deal.
// Used in: Structuring Hub, IC Submission page, Deal detail panels.

export const IC_STATES = {
  DRAFT:                  'DRAFT',
  SIGNED_OFF:             'SIGNED_OFF',
  SUBMITTED:              'SUBMITTED',
  UNDER_REVIEW:           'UNDER_REVIEW',
  APPROVED:               'APPROVED',
  APPROVED_WITH_CONDITIONS: 'APPROVED_WITH_CONDITIONS',
  REJECTED:               'REJECTED',
  ADMIN_RETRACT:          'ADMIN_RETRACT',
};

export const IC_STATE_CONFIG = {
  [IC_STATES.DRAFT]: {
    label: 'Draft',
    description: 'IC memo is being prepared by the deal team',
    color: '#94a3b8',
    bgColor: 'rgba(148,163,184,0.12)',
    step: 0,
    terminal: false,
  },
  [IC_STATES.SIGNED_OFF]: {
    label: 'Signed Off',
    description: 'Deal team lead has signed off for submission',
    color: '#6366f1',
    bgColor: 'rgba(99,102,241,0.12)',
    step: 1,
    terminal: false,
  },
  [IC_STATES.SUBMITTED]: {
    label: 'Submitted',
    description: 'IC submission lodged — awaiting committee review',
    color: '#0891b2',
    bgColor: 'rgba(8,145,178,0.12)',
    step: 2,
    terminal: false,
  },
  [IC_STATES.UNDER_REVIEW]: {
    label: 'Under Review',
    description: 'Investment Committee is actively reviewing',
    color: '#d97706',
    bgColor: 'rgba(217,119,6,0.12)',
    step: 3,
    terminal: false,
  },
  [IC_STATES.APPROVED]: {
    label: 'Approved',
    description: 'IC has approved the investment mandate',
    color: '#059669',
    bgColor: 'rgba(5,150,105,0.12)',
    step: 4,
    terminal: true,
  },
  [IC_STATES.APPROVED_WITH_CONDITIONS]: {
    label: 'Approved with Conditions',
    description: 'IC approved subject to specific conditions being met',
    color: '#0891b2',
    bgColor: 'rgba(8,145,178,0.12)',
    step: 4,
    terminal: true,
  },
  [IC_STATES.REJECTED]: {
    label: 'Rejected',
    description: 'IC did not approve the investment mandate',
    color: '#dc2626',
    bgColor: 'rgba(220,38,38,0.12)',
    step: 4,
    terminal: true,
  },
  [IC_STATES.ADMIN_RETRACT]: {
    label: 'Retracted',
    description: 'Submission retracted by platform administrator',
    color: '#6b7280',
    bgColor: 'rgba(107,114,128,0.12)',
    step: 4,
    terminal: true,
  },
};

// Ordered pipeline steps (non-terminal) for the stepper UI
export const IC_PIPELINE_STEPS = [
  IC_STATES.DRAFT,
  IC_STATES.SIGNED_OFF,
  IC_STATES.SUBMITTED,
  IC_STATES.UNDER_REVIEW,
];

// ─── Marketplace Access States ────────────────────────────────────────────────
// Represents how far an external investor has progressed through deal access.
// Used in: Opportunities listing, Opportunity Detail, External Deal Room.

export const MARKETPLACE_STATES = {
  LISTED:             'LISTED',
  ACCESS_REQUESTED:   'ACCESS_REQUESTED',
  NDA_PENDING:        'NDA_PENDING',
  NDA_SIGNED:         'NDA_SIGNED',
  DD_ACCESS:          'DD_ACCESS',
  SOFT_CIRCLED:       'SOFT_CIRCLED',
  DD_ACCESS_TIER_2:   'DD_ACCESS_TIER_2',
  COMMITTED:          'COMMITTED',
  CAP_TABLE_ENTRY:    'CAP_TABLE_ENTRY',
  CLOSING:            'CLOSING',
  CLOSED:             'CLOSED',
};

export const MARKETPLACE_STATE_CONFIG = {
  [MARKETPLACE_STATES.LISTED]: {
    label: 'Open',
    description: 'Opportunity is listed on the marketplace',
    color: '#059669',
    bgColor: 'rgba(5,150,105,0.10)',
    cta: 'Request Access',
    ctaAction: 'request_access',
    phase: 'discovery',
  },
  [MARKETPLACE_STATES.ACCESS_REQUESTED]: {
    label: 'Access Requested',
    description: 'Your access request is pending review',
    color: '#d97706',
    bgColor: 'rgba(217,119,6,0.10)',
    cta: 'Pending Review',
    ctaAction: null,
    phase: 'discovery',
  },
  [MARKETPLACE_STATES.NDA_PENDING]: {
    label: 'NDA Pending',
    description: 'NDA sent — signature required to proceed',
    color: '#6366f1',
    bgColor: 'rgba(99,102,241,0.10)',
    cta: 'Sign NDA',
    ctaAction: 'sign_nda',
    phase: 'qualification',
  },
  [MARKETPLACE_STATES.NDA_SIGNED]: {
    label: 'NDA Signed',
    description: 'NDA executed — initial data room access granted',
    color: '#0891b2',
    bgColor: 'rgba(8,145,178,0.10)',
    cta: 'View Data Room',
    ctaAction: 'view_deal_room',
    phase: 'qualification',
  },
  [MARKETPLACE_STATES.DD_ACCESS]: {
    label: 'Due Diligence',
    description: 'Full due diligence access granted',
    color: '#0891b2',
    bgColor: 'rgba(8,145,178,0.10)',
    cta: 'Continue DD',
    ctaAction: 'view_deal_room',
    phase: 'diligence',
  },
  [MARKETPLACE_STATES.SOFT_CIRCLED]: {
    label: 'Soft Circled',
    description: 'Indicative interest registered with deal team',
    color: '#7c3aed',
    bgColor: 'rgba(124,58,237,0.10)',
    cta: 'Confirm Interest',
    ctaAction: 'confirm_interest',
    phase: 'diligence',
  },
  [MARKETPLACE_STATES.DD_ACCESS_TIER_2]: {
    label: 'DD Tier 2',
    description: 'Enhanced access — final due diligence materials',
    color: '#7c3aed',
    bgColor: 'rgba(124,58,237,0.10)',
    cta: 'View Full DD',
    ctaAction: 'view_deal_room',
    phase: 'diligence',
  },
  [MARKETPLACE_STATES.COMMITTED]: {
    label: 'Committed',
    description: 'Commitment letter executed',
    color: '#059669',
    bgColor: 'rgba(5,150,105,0.10)',
    cta: 'View Commitment',
    ctaAction: 'view_commitment',
    phase: 'execution',
  },
  [MARKETPLACE_STATES.CAP_TABLE_ENTRY]: {
    label: 'Cap Table',
    description: 'Added to SPV cap table',
    color: '#059669',
    bgColor: 'rgba(5,150,105,0.10)',
    cta: 'View SPV',
    ctaAction: 'view_spv',
    phase: 'execution',
  },
  [MARKETPLACE_STATES.CLOSING]: {
    label: 'Closing',
    description: 'Transaction in closing process',
    color: '#0f766e',
    bgColor: 'rgba(15,118,110,0.10)',
    cta: 'View Closing',
    ctaAction: 'view_spv',
    phase: 'closing',
  },
  [MARKETPLACE_STATES.CLOSED]: {
    label: 'Closed',
    description: 'Transaction complete',
    color: '#3b4c4c',
    bgColor: 'rgba(59,76,76,0.10)',
    cta: 'View Holdings',
    ctaAction: 'view_holdings',
    phase: 'closed',
  },
};

// ─── Deal Pipeline States ─────────────────────────────────────────────────────
// Internal deal progress stages shown in the Pipeline page.

export const DEAL_PIPELINE_STATES = {
  ORIGINATION:  'origination',
  SCREENING:    'screening',
  IC_REVIEW:    'ic_review',
  APPROVED:     'approved',
  STRUCTURING:  'structuring',
  CLOSING:      'closing',
  ACTIVE:       'active',
  EXITED:       'exited',
};

export const DEAL_PIPELINE_CONFIG = {
  [DEAL_PIPELINE_STATES.ORIGINATION]:  { label: 'Origination', color: '#94a3b8', step: 0 },
  [DEAL_PIPELINE_STATES.SCREENING]:    { label: 'Screening',   color: '#6366f1', step: 1 },
  [DEAL_PIPELINE_STATES.IC_REVIEW]:    { label: 'IC Review',   color: '#d97706', step: 2 },
  [DEAL_PIPELINE_STATES.APPROVED]:     { label: 'Approved',    color: '#059669', step: 3 },
  [DEAL_PIPELINE_STATES.STRUCTURING]:  { label: 'Structuring', color: '#0891b2', step: 4 },
  [DEAL_PIPELINE_STATES.CLOSING]:      { label: 'Closing',     color: '#7c3aed', step: 5 },
  [DEAL_PIPELINE_STATES.ACTIVE]:       { label: 'Active',      color: '#0f766e', step: 6 },
  [DEAL_PIPELINE_STATES.EXITED]:       { label: 'Exited',      color: '#374151', step: 7 },
};

// ─── Helper Utilities ─────────────────────────────────────────────────────────

/** Returns the config for a given IC state */
export function getICStateConfig(state) {
  return IC_STATE_CONFIG[state] ?? IC_STATE_CONFIG[IC_STATES.DRAFT];
}

/** Returns the config for a given marketplace state */
export function getMarketplaceStateConfig(state) {
  return MARKETPLACE_STATE_CONFIG[state] ?? MARKETPLACE_STATE_CONFIG[MARKETPLACE_STATES.LISTED];
}

/** Returns true if the IC state has reached or passed a given step */
export function hasICReachedStep(currentState, targetState) {
  const current = IC_STATE_CONFIG[currentState]?.step ?? 0;
  const target  = IC_STATE_CONFIG[targetState]?.step ?? 0;
  return current >= target;
}
